import React, { useState, useMemo } from "react";
import {
  Box,
  Button,
  Chip,
  IconButton,
  MenuItem,
  Tooltip,
  Typography,
  Paper,
  Stack,
  LinearProgress,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import RefreshIcon from "@mui/icons-material/Refresh";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import {
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import Title from "../../components/Title";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchAllureSingleReport,
  fetchCaseReport,
} from "../../features/slices/Report";
import { AppDispatch, RootState } from "../../store";
import { useNotification } from "../../context/NotificationContext";
import { useNavigate } from "react-router-dom";
import { GlobalModal } from "../../components/GlobalModal";
import { JsonEditor } from "json-edit-react";
import FormattedDateCell from "../../components/FormattedDateCell";
import { useCasesByTagValues } from "../../api/tags/tags";
import { TagFilter } from "./components/TagFilter";
import { CaseTagsDialog } from "./components/CaseTagDialog";

// normalize tc_result (values use spaces or underscores across the stack)
const norm = (s) => String(s || "").toUpperCase().replace(/_/g, " ").trim();
const PASS = "#2e7d32";
const AMBER = "#d97706";
const RED = "#dc2626";
const GREY = "#6b7280";

const statusColor = (status) => {
  const s = norm(status);
  if (s === "PASSED") return PASS;
  if (s === "FAILED" || s === "FAILED ENSURE") return AMBER;
  if (["INTERNAL ERROR", "CMD ERROR", "ERROR", "INTERRUPTED", "ABORTED"].includes(s)) return RED;
  return GREY; // INIT / NO TESTS / RESERVED / queued
};
const isPassed = (s) => norm(s) === "PASSED";
const isFailed = (s) =>
  ["FAILED", "FAILED ENSURE", "INTERNAL ERROR", "CMD ERROR", "ERROR", "INTERRUPTED", "ABORTED"].includes(norm(s));

const Stat = ({ label, value, color }: { label: string; value: React.ReactNode; color?: string }) => (
  <Box sx={{ flex: 1, minWidth: 110 }}>
    <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 700, letterSpacing: 0.4 }}>
      {label.toUpperCase()}
    </Typography>
    <Typography sx={{ fontWeight: 800, fontSize: "1.4rem", color: color || "text.primary", lineHeight: 1.1 }}>
      {value}
    </Typography>
  </Box>
);

function CoverageSummary({ cases, hasFilter }: { cases: any[]; hasFilter: boolean }) {
  const total = cases.length;
  const passed = cases.filter((c) => isPassed(c.tc_result)).length;
  const failed = cases.filter((c) => isFailed(c.tc_result)).length;
  const notRun = total - passed - failed;
  const executed = passed + failed;
  const rate = executed ? Math.round((100 * passed) / executed) : 0;
  const rateColor = rate >= 80 ? PASS : rate >= 50 ? AMBER : RED;

  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, mb: 1.5 }}>
      <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ md: "center" }}>
        <Stat label="Covering cases" value={total} />
        <Stat label="Passed" value={passed} color={PASS} />
        <Stat label="Failed / errored" value={failed} color={failed ? RED : "text.primary"} />
        <Stat label="Not run" value={notRun} color={GREY} />
        <Box sx={{ flex: 2, minWidth: 200 }}>
          <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
            <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 700, letterSpacing: 0.4 }}>
              PASS RATE {executed ? "" : "(nothing executed yet)"}
            </Typography>
            <Typography variant="caption" sx={{ fontWeight: 800, color: rateColor }}>
              {executed ? `${rate}%` : "—"}
            </Typography>
          </Stack>
          <LinearProgress
            variant="determinate"
            value={executed ? rate : 0}
            sx={{
              height: 8,
              borderRadius: 4,
              bgcolor: alpha(GREY, 0.18),
              "& .MuiLinearProgress-bar": { bgcolor: rateColor, borderRadius: 4 },
            }}
          />
        </Box>
      </Stack>
      {!hasFilter && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
          Showing all cases. Pick a product tag type &amp; value above to see coverage for a specific product/feature.
        </Typography>
      )}
    </Paper>
  );
}

export default function CasesByTagsPage() {
  const [selectedTags, setSelectedTags] = useState([]);
  const [metaModal, setMetaModal] = useState(false);
  const [selectedMetadata, setSelectedMetadata] = useState({});
  const [tagDialogOpen, setTagDialogOpen] = useState(false);
  const [dialogTags, setDialogTags] = useState([]);
  const dispatch = useDispatch<AppDispatch>();
  const { user } = useSelector((state: RootState) => state.auth);
  const { notify } = useNotification();
  const navigate = useNavigate();
  const selectedTagIds = selectedTags.map((t) => t.id);
  const [filterState, setFilterState] = useState({ types: [], values: [] });

  const {
    data: cases = [],
    isLoading,
    refetch,
    isFetching,
  } = useCasesByTagValues(filterState.values);

  const columns = useMemo(
    () => [
      { accessorKey: "id", header: "ID" },
      {
        accessorKey: "allure",
        header: "Allure",
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ row }) => (
          <Button
            onClick={() =>
              handleAllure(Number(row.original.suite_id), Number(row.id))
            }
            disabled={!row.original.report_exists}
          >
            Report
          </Button>
        ),
      },
      { accessorKey: "suite_id", header: "Suite ID" },
      {
        accessorKey: "title",
        header: "Title",
        Cell: ({ cell }) => (
          <Tooltip title={cell.getValue()}>
            <span>{cell.getValue()}</span>
          </Tooltip>
        ),
      },

      {
        accessorKey: "case_tags_dialog",
        header: "Tags",
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ row }) => (
          <Button
            size="small"
            variant="outlined"
            onClick={() => {
              setDialogTags(row.original.case_tags || []);
              setTagDialogOpen(true);
            }}
          >
            View Tags
          </Button>
        ),
      },
      {
        accessorKey: "tc_result",
        header: "Result",
        enableSorting: false,
        Cell: ({ cell }) => {
          const v = cell.getValue<string>();
          const color = statusColor(v);
          return (
            <Chip
              size="small"
              label={v || "—"}
              sx={{
                fontWeight: 700,
                fontSize: "0.7rem",
                color,
                bgcolor: alpha(color, 0.12),
                border: `1px solid ${alpha(color, 0.32)}`,
              }}
            />
          );
        },
      },
      {
        accessorKey: "executed_at",
        header: "Execute At",
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ cell }) => <FormattedDateCell value={cell.getValue()} />,
      },

      {
        accessorKey: "file_name",
        header: "File Name",
        enableColumnFilter: false,
        enableSorting: false,
      },
      {
        accessorKey: "manifest",
        header: "Manifest",
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ cell }) => (
          <Button onClick={() => handleMetaOpen(cell.getValue())}>View</Button>
        ),
      },
    ],
    [filterState]
  );

  function handleMetaOpen(metadata) {
    setSelectedMetadata(metadata);
    setMetaModal(true);
  }

  async function handleDownload(caseId) {
    try {
      const result = await dispatch(fetchCaseReport(Number(caseId))).unwrap();
      const url = window.URL.createObjectURL(
        new Blob([new Uint8Array(result.data)], { type: "application/zip" })
      );
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `case_${caseId}.zip`);
      document.body.appendChild(link);
      link.click();
      notify("Downloaded report.", "success");
    } catch (error) {
      console.error("Error downloading report:", error);
      notify("Failed to download report.", "error");
    }
  }

  function handleEdit(id) {
    navigate(`/cases/${id}/edit`);
  }
  function handleView(id) {
    navigate(`/cases/${id}/view`);
  }
  async function handleAllure(suiteId, caseId) {
    if (!user) {
      notify("User is not authenticated", "error");
      return;
    }
    try {
      await dispatch(
        fetchAllureSingleReport({ userId: user?.id || 0, suiteId, caseId })
      ).unwrap();
    } catch (error) {
      notify("Error fetching report", "error");
      console.error("Error:", error);
    }
  }

  const table = useMaterialReactTable({
    columns,
    data: cases,
    mrtTheme: (theme) => ({
      baseBackgroundColor: theme.palette.background.paper,
    }),
    muiTablePaperProps: {
      elevation: 0,
      sx: { borderRadius: 2, border: "1px solid", borderColor: "divider", overflow: "hidden" },
    },
    muiTableContainerProps: {
      sx: { maxHeight: "calc(78vh - 112px)" },
    },
    muiTableHeadCellProps: {
      sx: {
        bgcolor: "background.level1",
        fontWeight: 800,
        fontSize: "0.72rem",
        letterSpacing: 0.4,
        textTransform: "uppercase",
        color: "text.secondary",
      },
    },
    muiTableBodyRowProps: { sx: { "&:hover td": { bgcolor: "action.hover" } } },
    muiTableBodyCellProps: { sx: { borderColor: "divider", py: 0.75 } },
    muiPaginationProps: {
      color: "secondary",
      rowsPerPageOptions: [10, 25, 50, 100],
      shape: "rounded",
      variant: "outlined",
    },
    paginationDisplayMode: "pages",
    columnFilterDisplayMode: "popover",
    initialState: {
      pagination: { pageSize: 25, pageIndex: 0 },
      density: "compact",
      showGlobalFilter: true,
      columnPinning: {
        right: ["mrt-row-actions"],
      },
      sorting: [
        {
          id: "created_at",
          desc: false,
        },
      ],
      columnVisibility: { sys_result: false },
    },
    enableColumnResizing: true,
    enableColumnOrdering: false,
    enableStickyHeader: true,
    enableRowActions: true,
    enableFacetedValues: true,
    manualPagination: false,
    layoutMode: "grid-no-grow",
    getRowId: (row) => String(row.id),
    renderTopToolbarCustomActions: ({ table }) => (
      <Tooltip arrow title="Refresh Data">
        <IconButton onClick={() => refetch()}>
          <RefreshIcon />
        </IconButton>
      </Tooltip>
    ),
    renderRowActionMenuItems: ({ closeMenu, row }) => [
      <MenuItem
        key={0}
        onClick={() => {
          handleEdit(Number(row.id));
          closeMenu();
        }}
        sx={{ m: 0 }}
      >
        Edit
      </MenuItem>,
      <MenuItem
        key={1}
        onClick={() => {
          handleView(Number(row.id));
          closeMenu();
        }}
        sx={{ m: 0 }}
      >
        View
      </MenuItem>,
      <MenuItem
        key={2}
        onClick={() => {
          handleDownload(Number(row.id));
          closeMenu();
        }}
        sx={{ m: 0 }}
      >
        Download
      </MenuItem>,
    ],
    state: {
      isLoading,
      showProgressBars: isFetching,
    },
  });

  const hasFilter = (filterState.values?.length || 0) > 0 || (filterState.types?.length || 0) > 0;

  return (
    <Box sx={{ p: 2 }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ sm: "center" }}
        justifyContent="space-between"
        spacing={1}
        sx={{ mb: 1.5 }}
      >
        <Box>
          <Title>Test Coverage by Product</Title>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, maxWidth: 640 }}>
            Pick a product tag type and value to see every test case that covers it and how it's doing —
            traceability from product features to test results.
          </Typography>
        </Box>
        <Button
          variant="outlined"
          size="small"
          startIcon={<CategoryOutlinedIcon />}
          endIcon={<OpenInNewIcon sx={{ fontSize: 15 }} />}
          onClick={() => navigate("/management/products")}
          sx={{ whiteSpace: "nowrap" }}
        >
          Manage products &amp; tags
        </Button>
      </Stack>

      <TagFilter value={filterState} onChange={setFilterState} />

      <CoverageSummary cases={cases} hasFilter={hasFilter} />

      <MaterialReactTable table={table} />
      {selectedTagIds.length > 0 && !cases.length && (
        <Typography color="error" mt={2}>
          No cases match ALL selected tags.
        </Typography>
      )}
      <CaseTagsDialog
        open={tagDialogOpen}
        onClose={() => setTagDialogOpen(false)}
        caseTags={dialogTags}
        selectedTags={filterState.values}
        selectedTypes={filterState.types}
      />
      <GlobalModal
        open={metaModal}
        onClose={() => setMetaModal(false)}
        title="Manifest"
      >
        <JsonEditor
          rootName={"manifest"}
          minWidth={"100%"}
          data={selectedMetadata}
          restrictAdd={true}
          restrictDelete={true}
          restrictEdit={true}
        />
      </GlobalModal>
    </Box>
  );
}
